import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  status: "active" | "revoked";
}

const LIST_KEY = (projectId: number) =>
  ["admin", "api-keys", projectId] as const;

export function useApiKeys(projectId: number | null) {
  return useQuery<{ items: ApiKey[] }>({
    queryKey: LIST_KEY(projectId ?? 0),
    queryFn: () => api(`/admin/projects/${projectId}/api-keys`),
    enabled: projectId !== null && projectId > 0,
    staleTime: 10_000,
  });
}

export function useCreateApiKey(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<{ ok: true; id: number; name: string; prefix: string; secret: string }>(
        `/admin/projects/${projectId}/api-keys`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(projectId) }),
  });
}

export function useRevokeApiKey(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: number) =>
      api<{ ok: true }>(`/admin/projects/${projectId}/api-keys/${keyId}/revoke`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(projectId) }),
  });
}
